import { useState, useEffect } from 'react';
import { JobWithRelations } from '@/types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useUpdateJobFinancials } from '@/hooks/useJobs';
import { useToast } from '@/hooks/use-toast';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { useVisibility } from '@/hooks/useVisibility';
import { useJobPayments } from '@/hooks/useJobPayments';
import { netFromGross, grossFromNet, formatTaxSuffix } from '@/utils/tax';

interface JobFinancialSummaryProps {
  job: JobWithRelations;
}

export function JobFinancialSummary({ job }: JobFinancialSummaryProps) {
  const { canSeeProfit } = useVisibility();
  const [revenue, setRevenue] = useState(job.invoice_total?.toString() || '');
  const [partsCost, setPartsCost] = useState(job.total_parts_cost?.toString() || '0');
  const [tollCost, setTollCost] = useState(job.toll_cost?.toString() || '0');
  const updateFinancials = useUpdateJobFinancials();
  const { toast } = useToast();
  const tax = useCompanyTax();
  const { data: jobPayments = [] } = useJobPayments(job.id);

  // Update local state when job data changes (after refresh)
  useEffect(() => {
    setRevenue(job.invoice_total?.toString() || '');
    setPartsCost(job.total_parts_cost?.toString() || '0');
    setTollCost(job.toll_cost?.toString() || '0');
  }, [job.invoice_total, job.total_parts_cost, job.toll_cost]);

  // Live calculations
  const revenueNum = parseFloat(revenue) || 0;
  const partsCostNum = parseFloat(partsCost) || 0;
  const tollCostNum = parseFloat(tollCost) || 0;
  const revenueBeforeGst = netFromGross(revenueNum, tax);

  // Card fee rate comes from company settings. The database trigger charges the fee
  // on CARD payments only, so the live preview must do the same — charging it on
  // total revenue overstated the fee on cash and bank-transfer jobs.
  const cardFeeRate = tax.cardFeeRate;
  const cardPaymentTotal = jobPayments
    .filter((p) => p.payment_method === 'card' || p.payment_method === 'square')
    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const totalCardFees = cardPaymentTotal * (cardFeeRate / 100);
  
  // Job profit = revenue before GST - parts - tolls - card fees
  const jobProfitBeforeTech = revenueBeforeGst - partsCostNum - tollCostNum - totalCardFees;
  
  // Calculate tech commission based on the tech's commission percentage
  const techCommissionPct = job.techs?.commission_percentage ?? 0;
  const techCommission = jobProfitBeforeTech * (techCommissionPct / 100);
  
  // Company profit (card fees already deducted in job_profit_before_tech)
  const companyProfit = jobProfitBeforeTech - techCommission;
  const outstanding = revenueNum - job.amount_paid;

  const handleUpdate = () => {
    updateFinancials.mutate({
      jobId: job.id,
      invoice_total: revenueNum || undefined,
      total_parts_cost: partsCostNum || undefined,
      toll_cost: tollCostNum || undefined,
    }, {
      onSuccess: () => {
        toast({
          title: "Financials Updated",
          description: canSeeProfit
            ? "Tech commission and company profit have been recalculated automatically."
            : "Job totals have been recalculated automatically.",
        });
      }
    });
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold mb-4">💰 Financials</h3>
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Left Column: Inputs */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="revenue" className="text-base font-semibold">
              Total Job Revenue{tax.enabled ? ` (${formatTaxSuffix(tax)})` : ''}
            </Label>
            <Input
              id="revenue"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              className="h-12 text-lg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="parts" className="text-base font-semibold">Total Parts Cost</Label>
            <Input
              id="parts"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={partsCost}
              onChange={(e) => setPartsCost(e.target.value)}
              className="h-12 text-lg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="toll" className="text-base font-semibold">Toll Cost</Label>
            <Input
              id="toll"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={tollCost}
              onChange={(e) => setTollCost(e.target.value)}
              className="h-12 text-lg"
            />
          </div>

          <Button 
            onClick={handleUpdate} 
            disabled={updateFinancials.isPending}
            className="w-full h-12 text-base font-semibold mt-6"
          >
            {updateFinancials.isPending ? 'Updating...' : 'Update Financials'}
          </Button>
        </div>

        {/* Right Column: Calculations */}
        <div className="space-y-3 bg-muted/50 p-4 rounded-lg">
          <h4 className="font-semibold text-sm text-muted-foreground uppercase">Live Calculations</h4>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {tax.enabled ? `Revenue (${formatTaxSuffix(tax)}):` : 'Revenue:'}
              </span>
              <span className="font-medium">${revenueNum.toFixed(2)}</span>
            </div>

            {tax.enabled && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Revenue (ex. {tax.label}):</span>
                <span className="font-medium">${revenueBeforeGst.toFixed(2)}</span>
              </div>
            )}

            <div className="border-t border-border pt-2 flex justify-between">
              <span className="text-muted-foreground">- Parts Cost:</span>
              <span className="font-medium text-destructive">-${Math.abs(partsCostNum).toFixed(2)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">- Toll Cost:</span>
              <span className="font-medium text-destructive">-${Math.abs(tollCostNum).toFixed(2)}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">- Card Processing Fee ({cardFeeRate}%):</span>
              <span className="font-medium text-destructive">-${Math.abs(totalCardFees).toFixed(2)}</span>
            </div>

            {canSeeProfit && (
              <>
                <div className="border-t border-border pt-2 flex justify-between">
                  <span className="text-muted-foreground">Job Profit (before tech):</span>
                  <span className="font-semibold">${jobProfitBeforeTech.toFixed(2)}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-muted-foreground">- Tech Commission ({techCommissionPct}%):</span>
                  <span className="font-medium text-destructive">-${Math.abs(techCommission).toFixed(2)}</span>
                </div>

                <div className="border-t-2 border-primary pt-2 flex justify-between">
                  <span className="font-semibold">Company Profit:</span>
                  <span className="text-xl font-bold text-primary">${companyProfit.toFixed(2)}</span>
                </div>
              </>
            )}

            <div className="border-t border-border pt-2 mt-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount Paid:</span>
                <span className="font-medium text-primary">${job.amount_paid.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-muted-foreground">Outstanding:</span>
                <span className={`font-semibold ${outstanding > 0 ? 'text-destructive' : 'text-primary'}`}>
                  ${outstanding.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
